import { SharePointParentFolder } from 'shared'
import CreateEditFormItems from '../CreateEditFormItems'
import DetailsBody from './components/DetailsBody'
import { CreateEditFormItemsValues } from '../CreateEditFormItems/types'
import { toDisciplineValues } from '../DisciplinesFormItem/utils/toDisciplineValues'
import { useGlobalState } from 'context/GlobalStateContext'
import {
  SubmitFieldDesignChangeInput,
  DraftFieldDesignChangeInput,
} from 'schema'
import AdvancedCreateModal from 'components/CIMSModal/AdvancedCreateModal'
import { usePayItemSelectContext } from 'components/CIMSSelect/common/PayItemSelect/PayItemSelectContext'
import { useProjectCustomFieldsContext } from 'components/CustomFields/ProjectCustomFieldsContext'
import { toMoment } from 'utils/toMoment'
import { useFDCTrackerContext } from '../../../../FDCTrackerContext'
import { toConcurrenceLevelValue } from '../ConcurrenceLevelFormItem/utils/toConcurrenceLevelValue'
import { ExternalLinkEditorValues } from 'components/ExternalLinkEditor/types'
import { DetailsBodyValues } from './components/DetailsBody/types'
import { errorHandler } from 'hooks/errorHandler'

const DraftSubmitModal: React.FC = () => {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const {
    mutations: { draft, submit },
    metadata,
    fieldDesignChanges,
  } = useFDCTrackerContext()
  const { toMutateCustomFieldsInput, toCustomFieldEditorValue } =
    useProjectCustomFieldsContext()
  const { toggle, state, visible } = submit
  const { toLabeledValues, fromLabeledValues } = usePayItemSelectContext()
  return (
    <AdvancedCreateModal
      initialFormValues={{
        issue: state?.issue,
        number: state?.number.toString() ?? crypto.randomUUID(),
        resolution: state?.resolution,
        location: state?.location,
        concurrenceLevel: toConcurrenceLevelValue(state?.concurrenceLevel),
        disciplines: toDisciplineValues(state?.disciplines),
        drawingsAffected: state?.drawingsAffected,
        requestedApprovalDate: toMoment(state?.requestedApprovalDate),
        specsAffected: state?.specsAffected,
        externalLinks: state?.externalLinks?.map(
          ({ __typename, ...link }) => link
        ),
        payItems: toLabeledValues(
          state?.fdcToPayItemNoGeometries?.map(({ payItem }) => payItem)
        ),
        customFields: toCustomFieldEditorValue(state?.customFields),
      }}
      visible={visible}
      onClose={() => toggle(null)}
      details={<DetailsBody />}
      additionalInfo={
        <CreateEditFormItems event={!state ? 'create' : 'draft'} />
      }
      attachments={{
        sharePoint: {
          parent: SharePointParentFolder.FieldDesignChanges,
          folder: ({ number }) => number,
          generic: !state,
          skipCleanup: !!state,
        },
        externalLinks: {
          quickLinks: metadata?.fdcQuickLinks,
          loading: metadata?.loading,
        },
      }}
      ok={{
        title: ({ concurrenceLevel, issue, location, disciplines }) =>
          !!concurrenceLevel && !!issue && !!location && !!disciplines?.length
            ? 'Submit'
            : 'Draft',
        fn: async (store) => {
          if (!projectId) return errorHandler('projectId is not defined')
          const values = store as DetailsBodyValues &
            CreateEditFormItemsValues &
            ExternalLinkEditorValues
          const {
            location,
            disciplines,
            issue,
            number,
            drawingsAffected,
            requestedApprovalDate,
            resolution,
            specsAffected,
            payItems,
            externalLinks,
          } = values
          const concurrenceLevelId = values.concurrenceLevel?.value as string
          const disciplineIds = disciplines?.map(({ value }) => value as string)
          const input:
            | SubmitFieldDesignChangeInput
            | DraftFieldDesignChangeInput = {
            id: state?.id,
            projectId,
            number,
            issue,
            resolution,
            drawingsAffected,
            requestedApprovalDate: requestedApprovalDate?.toISOString(),
            specsAffected,
            payItemIds: fromLabeledValues(payItems),
            customFields: toMutateCustomFieldsInput(values.customFields),
            externalLinks,
          }
          if (concurrenceLevelId && location && !!disciplineIds?.length)
            await submit.fn({
              variables: {
                input: {
                  ...input,
                  concurrenceLevelId,
                  disciplineIds,
                  location,
                },
              },
            })
          else
            await draft.fn({
              variables: {
                input: {
                  ...input,
                  concurrenceLevelId,
                  disciplineIds,
                  location,
                },
              },
            })
          fieldDesignChanges?.refetch()
        },
      }}
    />
  )
}

export default DraftSubmitModal
